import { Component } from '../helpers/helper';
import { VTitle } from '../helpers/vtitle';
import { state } from '../state';
import { Icon } from './icon';
import { transport } from '../firebaseTransport';
import { openToast } from './toast';
import { ReactionPanel } from './reactionPanel';

export const ThinSidebar = Component(() => {
	const showReactionPanel = Vue.ref(false);
	const showAttentionMenu = Vue.ref(false);

	function toggleStore() {
		if (state.browsingApps) {
			state.stopBrowsingApps();
		} else {
			state.browseApps();
		}
	}

	function onAppClick() {
		state.closeApp();
	}

	function toggleLayout() {
		if (state.videoLayout === 'gallery') {
			state.videoLayout = 'speaker';
		} else {
			state.setVideoLayout('gallery');
		}
	}

	function raiseHand() {
		const me = state.myUser;
		if (!me) return;
		const newState = !me.handRaised;
		me.handRaised = newState;
		transport
			.broadcast({ type: 'raise_hand', raised: newState, user_id: state.user.id })
			.catch(err => console.error('raise_hand broadcast failed', err));
	}

	function toggleReactionPanel(e: MouseEvent) {
		e.stopPropagation();
		showReactionPanel.value = !showReactionPanel.value;
		showAttentionMenu.value = false;
	}

	function toggleAttentionMenu(e: MouseEvent) {
		e.stopPropagation();
		showAttentionMenu.value = !showAttentionMenu.value;
		showReactionPanel.value = false;
	}

	function setAttention(signal: 'lost' | 'help' | null) {
		transport.setAttention(signal).catch(err => console.error('setAttention failed', err));
		showAttentionMenu.value = false;
	}

	async function claimHost() {
		const ok = await transport.claimHost();
		if (!ok) openToast('Could not claim host — someone else already has it', true);
	}

	const showOverflowPanel = Vue.ref(false);
	const overflowFromIndex = Vue.ref(-1);
	const panelPos = Vue.reactive({ top: 0, left: 0 });
	const contentRef = Vue.ref<HTMLElement>(null);
	const moreBtnRef = Vue.ref<HTMLElement>(null);

	function dismissPopovers() {
		showReactionPanel.value = false;
		showAttentionMenu.value = false;
		showOverflowPanel.value = false;
	}

	function toggleOverflowPanel(e: MouseEvent) {
		e.stopPropagation();
		if (!showOverflowPanel.value && moreBtnRef.value) {
			const r = moreBtnRef.value.getBoundingClientRect();
			panelPos.top = r.top;
			panelPos.left = r.right + 8;
		}
		showOverflowPanel.value = !showOverflowPanel.value;
		showReactionPanel.value = false;
		showAttentionMenu.value = false;
	}

	function computeOverflow() {
		const container = contentRef.value;
		if (!container) return;
		const containerH = container.clientHeight;
		const GAP = 4;
		const MORE_SLOT = 46 + GAP; // reserve one button slot for "..."
		const items = Array.from(
			container.querySelectorAll(':scope > .ts-item')
		) as HTMLElement[];
		if (items.length === 0) { overflowFromIndex.value = -1; return; }

		let usedH = 0;
		let cut = -1;
		for (let i = 0; i < items.length; i++) {
			const itemH = items[i].offsetHeight;
			const gapBefore = i > 0 ? GAP : 0;
			if (usedH + gapBefore + itemH + MORE_SLOT > containerH) {
				cut = i;
				break;
			}
			usedH += gapBefore + itemH;
		}
		overflowFromIndex.value = cut;
	}

	Vue.onMounted(() => {
		const docHandler = (_e: Event) => { showOverflowPanel.value = false; };
		document.addEventListener('click', docHandler);
		Vue.nextTick(() => {
			computeOverflow();
			const ro = new ResizeObserver(() => {
				overflowFromIndex.value = -1;
				Vue.nextTick(computeOverflow);
			});
			const el = contentRef.value;
			if (el) ro.observe(el);
			Vue.onUnmounted(() => {
				ro.disconnect();
				document.removeEventListener('click', docHandler);
			});
		});
	});

	// Re-measure whenever content-affecting state changes
	Vue.watchEffect(() => {
		void [state.audioEnabled, state.isHost, state.hasChannel, state.appLoaded,
			state.myUser?.handRaised, state.myUser?.attentionSignal];
		overflowFromIndex.value = -1;
		Vue.nextTick(computeOverflow);
	});

	function renderButtons() {
		const items: any[] = [];

		items.push(<div class="group-label">Call</div>);
		if (state.audioEnabled) {
			items.push(
				<button key="mic" onClick={e => state.enableMic(state.micMuted)} class={{ live: !state.micMuted }}>
					<VTitle text={state.micMuted ? 'Enable Mic' : 'Mute Mic'} />
					{!state.micMuted && <Icon name="microphone" size="24px" />}
					{state.micMuted && <Icon name="microphone-slash" size="24px" />}
				</button>
			);
			items.push(
				<button key="cam" onClick={e => state.enableCam()} class={{ live: state.sharingCam }}>
					<VTitle text="Webcam" />
					<Icon name="video" size="24px" />
				</button>
			);
			items.push(
				<button key="screen" onClick={e => state.shareScreen()} class={{ live: state.sharingScreen }}>
					<VTitle text="Share Screen" />
					<Icon name="screen-share" size="24px" />
				</button>
			);
			items.push(
				<button key="hand" onClick={raiseHand} class={{ live: !!state.myUser?.handRaised }}>
					<VTitle text={state.myUser?.handRaised ? 'Lower Hand' : 'Raise Hand'} />
					<span style="font-size:22px;line-height:1">✋</span>
				</button>
			);
			items.push(
				<div key="react" style="position:relative">
					<button onClick={toggleReactionPanel}>
						<VTitle text="React" />
						<Icon name="speaking" size="24px" />
					</button>
					{showReactionPanel.value && (
						<ReactionPanel onClose={() => { showReactionPanel.value = false; }} />
					)}
				</div>
			);
			if (!state.isHost) {
				items.push(
					<div key="attention" style="position:relative">
						<button
							class={{
								live: !!state.myUser?.attentionSignal,
								'attention-help': state.myUser?.attentionSignal === 'help',
								'attention-lost': state.myUser?.attentionSignal === 'lost'
							}}
							onClick={toggleAttentionMenu}
						>
							<VTitle text="Attention Signal" />
							<Icon name="exclamation" size="24px" />
						</button>
						{showAttentionMenu.value && (
							<div class="attention-submenu" onClick={(e: MouseEvent) => e.stopPropagation()}>
								<button onClick={() => setAttention('lost')}>I'm lost 🤔</button>
								<button onClick={() => setAttention('help')}>Need help 🚨</button>
								{state.myUser?.attentionSignal && (
									<button onClick={() => setAttention(null)}>Clear signal ✓</button>
								)}
							</div>
						)}
					</div>
				);
			}
		}

		items.push(<div class="group-label">View</div>);
		const galleryDisabled = !state.canUseGallery && state.videoLayout !== 'gallery';
		items.push(
			<button
				key="layout"
				onClick={toggleLayout}
				disabled={galleryDisabled}
				class={{ live: state.videoLayout === 'gallery', disabled: galleryDisabled }}
			>
				<VTitle text={
					galleryDisabled
						? 'Gallery View unavailable while an app or screen share is active'
						: state.videoLayout === 'gallery' ? 'Speaker View' : 'Gallery View'
				} />
				{state.videoLayout === 'gallery'
					? <Icon name="video" size="24px" />
					: <Icon name="view-grid" size="24px" />
				}
			</button>
		);
		items.push(
			<button key="sidebar" class={{ 'user-button': true, live: state.expanded }} onClick={e => state.toggleSidebar()}>
				<VTitle text="Toggle Sidebar" />
				<Icon name="users" size="24px" />
				<span class="user-count-badge">{state.users.length}</span>
			</button>
		);

		items.push(<div class="group-label">Apps</div>);
		items.push(
			<button key="play" onClick={toggleStore} class={{ live: state.browsingApps }}>
				<VTitle text="Start App" />
				<Icon name="play" size="24px" />
			</button>
		);
		if (state.appLoaded) {
			items.push(
				<button key="stop" onClick={onAppClick} class={{ live: state.appLoaded }}>
					<VTitle text={'Stop ' + state.appName} />
					<Icon name="stop" size="24px" />
				</button>
			);
		}

		items.push(<div class="group-label">More</div>);
		items.push(
			<button key="share" onClick={e => (state.showShareDlg = true)} class={{ live: state.showShareDlg }}>
				<VTitle text="Share Link" />
				<Icon name="link-solid" size="24px" />
			</button>
		);
		items.push(
			<button key="settings" onClick={e => (state.showSettingsDlg = true)}>
				<VTitle text="Settings" />
				<Icon name="cog" size="24px" />
			</button>
		);
		items.push(
			<button key="help" onClick={e => (state.showSupportDlg = true)}>
				<VTitle text="help & feedback" />
				<Icon name="question" size="24px" />
			</button>
		);

		if (state.hasChannel) {
			items.push(<div class="group-label">Host</div>);
			if (!state.hasHost) {
				items.push(
					<button key="claim-host" onClick={claimHost}>
						<VTitle text="Become Host" />
						<Icon name="badge-check" size="24px" />
					</button>
				);
			}
			if (state.isHost) {
				items.push(
					<button key="lock" class={{ live: state.roomLocked }}
						onClick={() => transport.setRoomLocked(!state.roomLocked).catch(console.error)}
					>
						<VTitle text={state.roomLocked ? 'Unlock Room' : 'Lock Room'} />
						<Icon name={state.roomLocked ? 'lock' : 'lock-open'} size="24px" />
					</button>
				);
				items.push(
					<button key="mute-all" onClick={() => transport.broadcast({ type: 'host_mute_all' }).catch(console.error)}>
						<VTitle text="Mute All" />
						<Icon name="microphone-slash" size="24px" />
					</button>
				);
				items.push(
					<button key="stop-video" onClick={() => transport.broadcast({ type: 'host_stop_all_video' }).catch(console.error)}>
						<VTitle text="Stop All Video" />
						<Icon name="video-slash" size="24px" />
					</button>
				);
			}
		}

		return items;
	}

	return () => {
		const ofi = overflowFromIndex.value;
		const hasOvfl = ofi >= 0;
		const allItems = renderButtons();
		const panelItems = hasOvfl ? renderButtons().slice(ofi) : [];

		return (
			<div id="thin_sidebar" onClick={dismissPopovers}>
				<div ref={contentRef} class="thin-sidebar-content">
					{allItems.map((item, i) => (
						<div key={`ri-${i}`} class={{ 'ts-item': true, 'ts-item-hidden': hasOvfl && i >= ofi }}>
							{item}
						</div>
					))}
					<button
						ref={moreBtnRef}
						class={{ 'overflow-more-btn': true, 'ts-item-hidden': !hasOvfl, live: showOverflowPanel.value }}
						onClick={toggleOverflowPanel}
						title="More controls"
					>
						<VTitle text="More controls" />
						<span class="overflow-dots">•••</span>
					</button>
				</div>
				{hasOvfl && showOverflowPanel.value && (
					<Vue.Teleport to="body">
						<div
							class="overflow-panel"
							style={{
								top: panelPos.top + 'px',
								left: panelPos.left + 'px',
								height: `calc(100vh - ${panelPos.top}px - 10px)`
							}}
							onClick={(e: MouseEvent) => e.stopPropagation()}
						>
							{panelItems.map((item, i) => (
								<div key={`pi-${i}`} class="ts-panel-item">{item}</div>
							))}
						</div>
					</Vue.Teleport>
				)}
				<button class="danger hangup" onClick={e => state.hangup()}>
					<VTitle text="Leave Channel" />
					<Icon name="logout" size="24px" />
				</button>
			</div>
		);
	};
});
